import { useForm, UseFormReturn, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  providerEditorSchema,
  type ProviderEditorFormData,
} from '../schemas/provider-editor.schema';

/**
 * Hook for managing provider editor form state with React Hook Form
 * 
 * Features:
 * - Zod schema validation for all 90+ provider fields
 * - Uncontrolled inputs for optimal performance
 * - Form state tracking (dirty, touched, errors)
 * - Support for initial data (edit mode)
 * 
 * @param initialData - Optional initial form data for edit mode
 * @returns React Hook Form methods and state
 */
export function useProviderEditorState(
  initialData?: ProviderEditorFormData
): UseFormReturn<ProviderEditorFormData> {
  const defaultValues: Partial<ProviderEditorFormData> = initialData || {
    user: {
      email: '',
      firstName: '',
      lastName: '',
      profilePhoto: '',
      accountStatus: 'ACTIVE',
    },
    provider: {
      companyName: '',
      businessAddress: '',
      latitude: null,
      longitude: null,
      vettingStatus: 'PENDING',
      newClientAcceptance: true,
      isBquestPreferred: false,
      phoneNumber: '',
      websiteUrl: '',
      calendlyUrl: '',
      serviceOfferings: '',
      tagline: '',
      providerBio: '',
      companyOverview: '',
      priceRangeIndicator: null,
      priceDetailsText: '',
      heroMediaUrl: '',
      mediaType: '',
      introductionVideoUrl: '',
      professionalTitle: '',
      yearsExperience: null,
      yearsInBusiness: null,
      establishedYear: null,
      certificationsArray: [],
      professionalLicenses: [],
      awardsRecognitions: [],
      languages: [],
      professionalAssociations: [],
      payerSources: [],
      serviceRanges: [],
      targetAgeRange: '',
      carePhilosophy: '',
      whyThisWork: '',
      continuingEducation: '',
      emergencyResponseAvailable: false,
      emergencyResponseDetails: '',
      hoursDescription: '',
      placeId: '',
      socialHandles: null,
    },
    categoryIds: [],
    specialties: [],
    expertiseIds: [],
    payerOptionIds: [],
    mediaGallery: [],
  };

  const form = useForm<ProviderEditorFormData>({
    // ponytail: zod 4 infers a looser input type (fields with .default() become optional);
    // the form is typed on the parsed output, as it was under zod 3. Upgrade path: type the
    // form as useForm<z.input<...>, unknown, ProviderEditorFormData> and update consumers.
    resolver: zodResolver(providerEditorSchema) as Resolver<ProviderEditorFormData>,
    defaultValues,
    mode: 'onBlur', // Validate on blur for better UX
    reValidateMode: 'onChange', // Re-validate on change after first validation
  });

  return form;
}

/**
 * Type export for easier access to form methods
 */
export type UseProviderEditorStateReturn = ReturnType<typeof useProviderEditorState>;

